/**
* PURPOSE: Sección de listado de eventos/campeonatos disponibles en Winscore.
* RESPONSIBILITIES: Cargar eventos por permisos, company autorizada y búsqueda local sin romper roles existentes.
* COLLABORATORS: src/services/championship.js, src/services/company.js, src/utils/authUser.js, src/components/Cards/CardEvent/CardEvent.jsx
* ROUTE: src/sections/CampeonatosSection.jsx
*/
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import API_BASE_URL from "../config/config.js";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiSearch } from "react-icons/fi";

import CardEvent from "../components/Cards/CardEvent/CardEvent";
import { getCompany } from "../services/company.js";
import {
  getAuthorizedChampionshipsByCompany,
  getChampionshipsByCompany,
} from "../services/championship.js";
import {
  buildCampeonatosUrlByCompany,
  getUserCompanyId,
  getUserRole,
  isOneCompanyUser,
  parsePositiveInt,
} from "../utils/authUser.js";

const CampeonatosSection = () => {
  const [campeonatos, setCampeonatos] = useState([]);
  const [company, setCompany] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [search, setSearch] = useState("");

  const { user } = useSelector((state) => state.auth);
  const yearSelected = useSelector((state) => state.year.yearSelected);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawCompanyId = searchParams.get("idcompany");

  const urlCompanyId = useMemo(() => parsePositiveInt(rawCompanyId), [rawCompanyId]);
  const role = useMemo(() => getUserRole(user), [user]);
  const userCompanyId = useMemo(() => getUserCompanyId(user), [user]);
  const oneCompany = useMemo(() => isOneCompanyUser(user), [user]);

  const isRole4OneCompany = role === 4 && oneCompany;
  const isRole1OneCompany = role === 1 && oneCompany;
  const isForcedSingleCompanyUser = isRole4OneCompany || isRole1OneCompany;
  const isScopedMultiCompanyUser = [2, 3, 4].includes(role) && !oneCompany;
  const shouldUseAuthorizedCompanyEndpoint =
    Boolean(urlCompanyId) && [2, 3, 4].includes(role);

  const effectiveCompanyId = isForcedSingleCompanyUser ? userCompanyId : urlCompanyId;

  useEffect(() => {
    if (!user?.iduser) return;

    if (isForcedSingleCompanyUser) {
      if (userCompanyId) {
        if (urlCompanyId !== userCompanyId) {
          navigate(buildCampeonatosUrlByCompany(userCompanyId), { replace: true });
        }
        return;
      }

      if (urlCompanyId) navigate("/campeonatos", { replace: true });
      return;
    }

    if (isScopedMultiCompanyUser && !urlCompanyId) {
      navigate("/companies", { replace: true });
    }
  }, [
    user?.iduser,
    isForcedSingleCompanyUser,
    isScopedMultiCompanyUser,
    userCompanyId,
    urlCompanyId,
    navigate,
  ]);

  useEffect(() => {
    const getCampeonatos = async () => {
      setLoading(true);

      try {
        if (!user?.iduser || !yearSelected) {
          setCampeonatos([]);
          return;
        }

        if (isForcedSingleCompanyUser) {
          if (!userCompanyId) {
            setCampeonatos([]);
            return;
          }

          if (urlCompanyId !== userCompanyId) {
            setCampeonatos([]);
            return;
          }

          const championships = await getAuthorizedChampionshipsByCompany({
            iduser: user.iduser,
            idcompany: userCompanyId,
            periodo: Number(yearSelected),
          });

          setCampeonatos(championships);
          return;
        }

        if (shouldUseAuthorizedCompanyEndpoint) {
          const championships = await getAuthorizedChampionshipsByCompany({
            iduser: user.iduser,
            idcompany: urlCompanyId,
            periodo: Number(yearSelected),
          });

          setCampeonatos(championships);
          return;
        }

        if (urlCompanyId) {
          const championships = await getChampionshipsByCompany({
            idcompany: urlCompanyId,
            periodo: Number(yearSelected),
          });

          setCampeonatos(championships);
          return;
        }

        const responseTest = await axios.post(
          `${API_BASE_URL}/menu_championships_sports`,
          {
            iduser: user.iduser,
            periodo: Number(yearSelected),
            tipo: 1,
          }
        );

        const championships = Array.isArray(responseTest?.data?.championships)
          ? responseTest.data.championships
          : [];

        setCampeonatos(championships);
      } catch (error) {
        console.error("Error fetching championships:", error);
        setCampeonatos([]);
      } finally {
        setLoading(false);
      }
    };

    getCampeonatos();
  }, [
    user?.iduser,
    yearSelected,
    urlCompanyId,
    isForcedSingleCompanyUser,
    shouldUseAuthorizedCompanyEndpoint,
    userCompanyId,
  ]);

  useEffect(() => {
    let cancelled = false;

    const getCompanyInfo = async () => {
      if (!effectiveCompanyId) {
        setCompany(null);
        return;
      }

      setCompanyLoading(true);

      try {
        const data = await getCompany(effectiveCompanyId);
        if (!cancelled) setCompany(data && typeof data === "object" ? data : null);
      } catch (error) {
        console.log("Error fetching company info:", error);
        if (!cancelled) setCompany(null);
      } finally {
        if (!cancelled) setCompanyLoading(false);
      }
    };

    getCompanyInfo();

    return () => {
      cancelled = true;
    };
  }, [effectiveCompanyId]);

  const filteredCampeonatos = useMemo(() => {
    const term = String(search || "").trim().toLowerCase();

    if (!term) return campeonatos;

    return campeonatos.filter((campeonato) => {
      const name = String(campeonato?.name || "").toLowerCase();
      const place = String(campeonato?.place || "").toLowerCase();
      const periodo = String(campeonato?.periodo || "").toLowerCase();
      const organizedBy = String(campeonato?.organizedBy || "").toLowerCase();
      const subtitle = String(campeonato?.subTitleSports || "").toLowerCase();

      return (
        name.includes(term) ||
        place.includes(term) ||
        periodo.includes(term) ||
        organizedBy.includes(term) ||
        subtitle.includes(term)
      );
    });
  }, [campeonatos, search]);

  const companyInitials = useMemo(() => {
    const source = company?.abrev || company?.name || "EV";
    return String(source).trim().slice(0, 2).toUpperCase();
  }, [company]);

  const renderHeader = () => {
    if (effectiveCompanyId && (companyLoading || company)) {
      return (
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="inline-flex max-w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
              {company?.logo_path ? (
                <img
                  src={company.logo_path}
                  alt={company?.name || company?.abrev || "Company"}
                  className="h-full w-full object-contain p-1.5"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <span className="text-xs font-bold tracking-wide text-slate-600">
                  {companyInitials}
                </span>
              )}
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate text-lg font-bold tracking-tight text-slate-800 sm:text-xl">
                  {company?.name || "Eventos"}
                </h2>

                {company?.abrev ? (
                  <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-blue-700">
                    {company.abrev}
                  </span>
                ) : null}
              </div>

              <p className="mt-0.5 text-sm text-slate-500">
                Eventos de {company?.abrev || company?.name || "la organización"} para el periodo {yearSelected}
              </p>
            </div>
          </div>

          <div className="relative w-full md:max-w-md">
            <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar evento..."
              className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-12 pr-4 text-sm text-slate-700 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>
        </div>
      );
    }

    return (
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="inline-flex flex-col">
          <div className="flex items-center gap-3">
            <span className="inline-block h-8 w-1.5 rounded-full bg-blue-600" />
            <h2 className="text-2xl font-extrabold tracking-tight text-slate-800 sm:text-3xl">
              Eventos
            </h2>
          </div>
          <p className="mt-2 pl-4 text-sm text-slate-500 sm:text-base">
            Explora los eventos disponibles para el periodo seleccionado
          </p>
        </div>

        <div className="relative w-full md:max-w-md">
          <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar evento..."
            className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-12 pr-4 text-sm text-slate-700 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div>
        {renderHeader()}
        <p className="text-sm text-slate-500">Cargando eventos...</p>
      </div>
    );
  }

  return (
    <div>
      {renderHeader()}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-2">
        {filteredCampeonatos.length > 0 ? (
          filteredCampeonatos.map((campeonato, index) => (
            <div key={campeonato?.idchampionship || index}>
              <CardEvent event={campeonato} />
            </div>
          ))
        ) : (
          <div className="col-span-full rounded-2xl border border-slate-200 bg-white px-6 py-12 text-center text-gray-500 shadow-sm">
            {search.trim()
              ? "No se encontraron eventos con ese criterio de búsqueda."
              : "No se tiene registrado eventos para este usuario en el periodo seleccionado."}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampeonatosSection;
